import React, { useState, useContext } from "react";
import { View, StyleSheet } from "react-native";
import NtText from "../../components/NtText";
import colors from "../../config/colors";
import NtActivityIndicator from "../../components/NtActivityIndicator";
import UserContext from "../../context/UserContext";
import NtModalUnactiveButton from "../../components/NtModalUnactiveButton";
import NtModalActiveButton from "../../components/NtModalActiveButton";
import NtModalInputField from "../../components/NtModalInputField";
import NtModalInputTitle from "../../components/NtModalInputTitle";
import NtToggleRow from "../../components/NtToggleRow";
import Logger from "../../common/utils/Logger";
import { parseError } from "@ntconnect/framework/pbx";
import NtModalError from "../../components/NtModalError";
import { deleteVoicemailBox, updateVoicemailBox } from "../../api/voicemail";
import NtModalTitle from "../../components/NtModalTitle";
import NtModalSubtitle from "../../components/NtModalSubtitle";

// Backend stores `voicemail_local_after_email` as a boolean-ish string ("true"
// / "false") or a real boolean depending on tier — coerce defensively.
function asBool(value, fallback = false) {
  if (typeof value === "boolean") return value;
  if (typeof value === "string") return value.toLowerCase() === "true";
  return fallback;
}

function NtVoicemailUpdateForm({ item, onCancel, onUpdate, onDelete }) {
  const [password, setPassword] = useState(item.voicemail_password || "");
  const [description, setDescription] = useState(item.voicemail_description || "");
  const [attachFileToEmail, setAttachFileToEmail] = useState(
    item.voicemail_file === "attach",
  );
  const [keepLocalAfterEmail, setKeepLocalAfterEmail] = useState(
    asBool(item.voicemail_local_after_email, true),
  );

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const context = useContext(UserContext);

  const isRequiredFields = () => !!password;

  const handleUpdate = async () => {
    setLoading(true);
    setError(null);

    const params = {
      password,
      description,
      attachFileToEmail,
      keepLocalAfterEmail,
      status: true,
    };

    const response = await updateVoicemailBox(
      context.user?.domain,
      params,
      item.voicemail_id,
    );
    Logger("Voicemail Box Update Patch", response);

    if (response.ok) {
      if (onUpdate) onUpdate();
    } else {
      setError(parseError(response));
    }
    setLoading(false);
  };

  const handleDesable = async () => {
    setLoading(true);
    setError(null);

    const params = { password, status: false };
    const response = await updateVoicemailBox(
      context.user?.domain,
      params,
      item.voicemail_id,
    );
    Logger("Voicemail Box Update Patch", response);

    if (response.ok) {
      if (onUpdate) onUpdate();
    } else {
      setError(parseError(response));
    }
    setLoading(false);
  };

  const handleDelete = async () => {
    setLoading(true);
    setError(null);

    const response = await deleteVoicemailBox(
      context.user?.domain,
      item?.voicemail_id,
    );

    if (response.ok) {
      if (onDelete) onDelete();
    } else {
      setError(parseError(response));
    }
    setLoading(false);
  };

  return (
    <View style={styles.container}>
      <NtModalTitle>Update Voicemail Box</NtModalTitle>
      <NtModalSubtitle style={{ marginTop: 4 }}>
        Update Voicemail Box Settings{" "}
        <NtText style={{ fontWeight: "900" }}>{item.voicemail_id}</NtText>
      </NtModalSubtitle>

      <NtModalInputField
        containerStyle={{ marginTop: 15 }}
        title="Password *"
        placeholder="Numeric PIN (digits only)"
        value={password}
        setValue={(text) => setPassword(text.replace(/\D/g, ""))}
        secureTextEntry={true}
        showEye={true}
        keyboardType="numeric"
      />
      <NtModalInputField
        containerStyle={{ marginTop: 15 }}
        title="Notes"
        placeholder="i.e. Unified Voicemail for Front Desk"
        value={description}
        setValue={setDescription}
      />

      <NtModalInputTitle style={{ marginTop: 20, marginLeft: 4 }}>
        Email behavior
      </NtModalInputTitle>
      <View style={{ marginTop: 6 }}>
        <NtToggleRow
          value={attachFileToEmail}
          onChange={setAttachFileToEmail}
          label="Attach audio to email"
          sub="Include the voicemail .wav as an attachment on the notification email."
        />
        <View style={{ height: 8 }} />
        <NtToggleRow
          value={keepLocalAfterEmail}
          onChange={setKeepLocalAfterEmail}
          label="Keep local copy after email"
          sub="Keep the message in the voicemail box even after the email is sent."
        />
      </View>

      <NtModalError error={error} containerStyle={{ marginTop: 6 }} />

      <View
        style={{
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "space-between",
          marginTop: 40,
        }}
      >
        <View style={{ flexDirection: "row", alignItems: "center" }}>
          <NtModalActiveButton
            containerStyle={{ backgroundColor: "red" }}
            title="Delete"
            icon="delete"
            onPress={handleDelete}
            enabled={true}
          />
          <NtModalActiveButton
            containerStyle={{ marginLeft: 15 }}
            title="Disable"
            onPress={handleDesable}
            enabled={true}
          />
        </View>

        <View style={{ flexDirection: "row", alignItems: "center" }}>
          {loading && (
            <NtActivityIndicator
              color={colors.blue}
              size="small"
              containerStyle={{ marginRight: 25 }}
            />
          )}
          <NtModalUnactiveButton title="Cancel" onPress={onCancel} />
          <NtModalActiveButton
            containerStyle={{ marginLeft: 15 }}
            title={
              item?.voicemail_enabled == "false"
                ? "Update & Activate"
                : "Update"
            }
            onPress={() => {
              if (isRequiredFields()) handleUpdate();
            }}
            enabled={isRequiredFields()}
          />
        </View>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    minWidth: 500,
    maxWidth: 600,
    padding: 15,
  },
});

export default NtVoicemailUpdateForm;
